/**
 * MessageTimeline — WhatsApp-style bubble list for the conversation detail
 * page (Story 7.10).
 *
 * Layout:
 *   - inbound  → left, neutral background
 *   - outbound → right, green-tinted
 *   - each bubble: content, small timestamp, type icon (text / audio / etc.),
 *     intent pill (outbound only), token footer (↑ in / ↓ out), and a
 *     collapsible `<details>` for the raw AI JSON (outbound with `aiJson`).
 *
 * Intentionally dependency-light: uses native `<details>` for collapsibility
 * (no extra Radix primitive needed) so the tests can assert open/closed state
 * via the native DOM.
 */

import { FileAudio, FileImage, FileText, FileType, Info, UserCheck } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { formatDateTimeCompact } from '@/lib/format-date';

export type MessageDirection = 'inbound' | 'outbound';
export type MessageType = 'text' | 'template' | 'audio' | 'image' | 'system';

export interface TimelineMessage {
  id: string;
  direction: MessageDirection;
  messageType: MessageType;
  content: string;
  intent: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  aiJson: unknown;
  /** Story 12.3 — true quando outbound foi enviada manualmente pelo operador. */
  sentByHuman?: boolean;
  /** Story 12.3 — nome do operador (snapshot). */
  sentByUserName?: string | null;
  createdAt: string;
}

const TYPE_ICON: Record<MessageType, ComponentType<SVGProps<SVGSVGElement>>> = {
  text: FileText,
  template: FileType,
  audio: FileAudio,
  image: FileImage,
  system: Info,
};

function formatTime(iso: string): string {
  return formatDateTimeCompact(iso) || iso;
}

export function MessageTimeline({ messages }: { messages: TimelineMessage[] }) {
  if (messages.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        Sem mensagens nesta conversa.
      </div>
    );
  }

  return (
    <ol className="space-y-3" data-testid="message-timeline">
      {messages.map((m) => (
        <TimelineBubble key={m.id} message={m} />
      ))}
    </ol>
  );
}

function TimelineBubble({ message }: { message: TimelineMessage }) {
  const isOutbound = message.direction === 'outbound';
  const TypeIcon = TYPE_ICON[message.messageType] ?? FileText;

  const alignment = isOutbound ? 'justify-end' : 'justify-start';
  const bubbleStyle = isOutbound
    ? 'bg-green-500/15 text-foreground border-green-500/30'
    : 'bg-muted text-foreground border-border';

  return (
    <li data-testid={`timeline-bubble-${message.direction}`} className={`flex ${alignment}`}>
      <div
        className={`max-w-[90%] rounded-lg border px-3 py-2 text-sm sm:max-w-[85%] ${bubbleStyle}`}
      >
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <TypeIcon aria-hidden className="h-3 w-3" />
          <span>{message.messageType}</span>
          <span aria-hidden>•</span>
          <time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
          {isOutbound && message.sentByHuman ? (
            <span
              data-testid="sent-by-human-badge"
              title={
                message.sentByUserName
                  ? `Enviado manualmente por ${message.sentByUserName}`
                  : 'Enviado manualmente pelo operador'
              }
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-200"
            >
              <UserCheck aria-hidden className="h-2.5 w-2.5" />
              {message.sentByUserName ?? 'humano'}
            </span>
          ) : null}
          {isOutbound && !message.sentByHuman && message.intent ? (
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
              {message.intent}
            </span>
          ) : null}
        </div>
        <p className="mt-1 whitespace-pre-wrap break-words">{message.content}</p>
        {(message.tokensIn !== null || message.tokensOut !== null) && (
          <div className="mt-1 text-[10px] text-muted-foreground">
            {message.tokensIn !== null ? `↑ ${message.tokensIn}` : ''}
            {message.tokensIn !== null && message.tokensOut !== null ? ' / ' : ''}
            {message.tokensOut !== null ? `↓ ${message.tokensOut}` : ''}
          </div>
        )}
        {isOutbound && message.aiJson !== null && message.aiJson !== undefined ? (
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer select-none text-muted-foreground hover:underline">
              raw AI JSON
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded bg-background p-2 text-[11px]">
              {JSON.stringify(message.aiJson, null, 2)}
            </pre>
          </details>
        ) : null}
      </div>
    </li>
  );
}

export default MessageTimeline;
